import { useEffect, useRef, useCallback } from 'react';
import { devContainerAPI } from '@/lib/dev-containers/api';

// Heartbeat interval: 5 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

// Threshold for detecting long absence (e.g., laptop sleep)
const LONG_ABSENCE_THRESHOLD = 30 * 60 * 1000; // 30 minutes

interface UseDevContainerHeartbeatOptions {
  devContainerId: string | undefined;
  onContainerPaused?: () => void;
}

/**
 * Hook to send periodic heartbeats to keep dev container active
 * Only sends heartbeats when:
 * - devContainerId is provided
 * - Tab is visible (not hidden)
 *
 * Also detects when browser wakes from sleep and container may have been paused.
 * If absent > 30min, checks container status and triggers onContainerPaused callback.
 */
export function useDevContainerHeartbeat({
  devContainerId,
  onContainerPaused,
}: UseDevContainerHeartbeatOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeartbeatRef = useRef<number>(0);

  const sendHeartbeat = useCallback(async () => {
    if (!devContainerId) return;

    // Only send if tab is visible
    if (document.visibilityState !== 'visible') return;

    try {
      await devContainerAPI.heartbeat(devContainerId);
      lastHeartbeatRef.current = Date.now();
    } catch (error) {
      // Silently fail - heartbeat is best-effort
      console.debug('[Heartbeat] Failed to send heartbeat:', error);
    }
  }, [devContainerId]);

  useEffect(() => {
    if (!devContainerId) return;

    // Skip immediate heartbeat - access-url already updated lastAccessedAt during project load
    // Just initialize the timestamp for visibility change detection
    lastHeartbeatRef.current = Date.now();

    // Set up interval for periodic heartbeats
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Handle visibility change - check container status and send heartbeat when tab becomes visible
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;

      // If we've been "asleep" for a long time, container might be paused
      if (timeSinceLastHeartbeat >= LONG_ABSENCE_THRESHOLD && devContainerId) {
        try {
          const data = await devContainerAPI.getDevContainer(devContainerId);
          if (data.status === 'paused') {
            console.debug('[Heartbeat] Container paused during sleep, triggering resume');
            onContainerPaused?.();
            return; // Don't send heartbeat, let resume flow handle it
          }
        } catch (error) {
          console.debug('[Heartbeat] Failed to check container status:', error);
        }
      }

      // Container still running - send heartbeat as normal
      if (timeSinceLastHeartbeat >= HEARTBEAT_INTERVAL_MS) {
        sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [devContainerId, sendHeartbeat, onContainerPaused]);
}
